import { useEffect, useState } from "react"

import { RatingStars, type Advisor } from "@/features/advisors"

import { useChat } from "../hooks/use-chat"
import { connectCopy } from "../config/chat-copy"

import ShieldIcon from "@/assets/img/live-chat/shield_icon_new.svg?react"

const randomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min

interface ConnectingAdvisorProps {
  selectedAdvisor: Advisor
  onStart: () => void
}

/** Full-screen "connecting you to advisor" gate shown before the chat opens. */
export function ConnectingAdvisor({ selectedAdvisor, onStart }: ConnectingAdvisorProps) {
  const { setSelectedAdvisor, preSelectedChatMessage } = useChat()
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    setIsLoading(true)
    const isLocalhost = typeof window !== "undefined" && window.location.hostname === "localhost"
    const timeoutMs = isLocalhost ? 1000 : randomInt(5, 8) * 1000
    const timer = setTimeout(() => setIsLoading(false), timeoutMs)
    return () => clearTimeout(timer)
  }, [selectedAdvisor])

  return (
    <div className="fixed inset-x-0 bottom-0 top-0 z-[100] mx-auto flex max-w-app flex-col items-center bg-dark-base px-4 py-6">
      <h2 className="mb-2 text-center font-display text-2xl font-medium text-purple-light">
        {connectCopy.connectTitle}
      </h2>
      <p className="text-center font-body text-base text-purple-light/70">
        {connectCopy.connectSubtitle}
      </p>

      <div className="flex w-full flex-1 flex-col items-center justify-center">
        <div className="relative my-5 flex h-80 w-60 flex-col justify-end overflow-hidden rounded-[20px] shadow-[0_0_40px_0_rgba(13,8,28,0.5)]">
          <img
            src={selectedAdvisor.photo}
            alt={selectedAdvisor.name}
            className="absolute inset-0 size-full object-cover"
          />
          <div className="absolute right-2 top-2 flex items-center gap-1 rounded-2xl bg-dark-base/80 py-1.5 pl-2.5 pr-3 font-body text-xs text-green">
            <span className="size-2 rounded-full bg-green" />
            Online
          </div>
          <div className="relative flex h-40 flex-col justify-end bg-gradient-to-b from-transparent to-card-purple p-4">
            <p className="font-display text-xl font-medium text-white">{selectedAdvisor.name}</p>
            <p className="font-body text-sm text-purple-light/70">{selectedAdvisor.exp}</p>
            <div className="mb-3 mt-0.5 flex items-center gap-1">
              <RatingStars rating={selectedAdvisor.rating} className="h-4 w-20 text-gold" />
              <span className="font-body text-sm font-semibold text-gold">
                {selectedAdvisor.rating}
              </span>
              <span className="font-body text-sm text-purple-light/70">
                {selectedAdvisor.reviews}
              </span>
            </div>
          </div>
        </div>

        {preSelectedChatMessage && (
          <button
            type="button"
            onClick={() => setSelectedAdvisor(null)}
            className="mb-4 flex h-11 items-center gap-2 rounded-[80px] border border-purple-light/20 pl-6 pr-4 font-body text-base font-semibold text-white"
          >
            {connectCopy.changeAdvisor}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={onStart}
        disabled={isLoading}
        className="flex h-14 w-full items-center justify-center gap-2 rounded-[50px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white disabled:bg-none disabled:bg-purple-light/10 disabled:text-purple-light/70"
      >
        {isLoading ? (
          <>
            <span className="size-5 animate-spin rounded-full border-2 border-purple-light border-t-transparent" />
            {connectCopy.connectingStatus}
          </>
        ) : (
          connectCopy.startChat
        )}
      </button>
      <p className="mt-4 flex items-center gap-1.5 font-body text-sm text-purple-light/70 [&_svg]:size-6">
        <ShieldIcon />
        {connectCopy.secure}
      </p>
    </div>
  )
}
